import {
	Assessment,
	CancelOutlined,
	CheckCircleOutlineOutlined,
	DarkMode,
	EventAvailableOutlined,
	PersonAddAltOutlined,
	PersonSearchOutlined,
	PublishedWithChangesOutlined,
	RemoveCircleOutlineOutlined,
	WbSunny,
} from "@mui/icons-material";
import { Box, Paper, Tooltip, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { DateTime } from "luxon";
import { type FC, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthProvider";
import { useHospitalData } from "../../contexts/HospitalDataProvider";
import { useDepartmentColor } from "../../hooks/useDepartmentColor";
import { SHIFT_DAY, SHIFT_NIGHT_FALLBACK } from "../../theme/tokens";
import { trackErrorEvent, trackEvent } from "../../utils/analytics";
import { type NotificationResponse, NotificationType, ShiftType } from "../../utils/models";
import { buildTeamNeedsDecisionUrl } from "../team/statusDecisions/deepLink";

interface NotificationItemProps {
	notification: NotificationResponse;
	markNotificationClicked: (notificationId: number) => Promise<boolean>;
}

const getNotificationIcon = (notificationType: string, shiftType: ShiftType, title?: string) => {
	const iconStyle = { fontSize: "1.25rem", color: "#333333" };

	// Handle special case for Remove From Shift (the title doesn't match the notification type)
	if (title?.includes("Remove From Shift")) {
		return <RemoveCircleOutlineOutlined sx={{ ...iconStyle }} />;
	}

	switch (notificationType) {
		case NotificationType.OpenShift:
		case NotificationType.ReplaceShift:
		case NotificationType.SchedulePublished:
			return <EventAvailableOutlined sx={{ ...iconStyle }} />;
		case NotificationType.CallOff:
		case NotificationType.RemovedFromShift:
			return <RemoveCircleOutlineOutlined sx={{ ...iconStyle }} />;
		case NotificationType.AddedToShift:
			return <PersonAddAltOutlined sx={{ ...iconStyle }} />;
		case NotificationType.Approved:
			return <CheckCircleOutlineOutlined sx={{ ...iconStyle }} />;
		case NotificationType.ApprovedReplace:
			return <PublishedWithChangesOutlined sx={{ ...iconStyle }} />;
		case NotificationType.Declined:
		case NotificationType.DeclinedReplace:
			return <CancelOutlined sx={{ ...iconStyle }} />;
		case NotificationType.PerformanceReport:
			return <Assessment sx={{ ...iconStyle }} />;
		case NotificationType.EmployeeStatusDecision:
			return <PersonSearchOutlined sx={{ ...iconStyle }} />;
		// case NotificationType.ReplaceShift:
		// return <SwapHorizOutlined sx={{ ...iconStyle }} />;
		default:
			// Default icon based on shift type if no specific type matches
			return shiftType === ShiftType.Day ? <WbSunny sx={{ ...iconStyle }} /> : <DarkMode sx={{ ...iconStyle }} />;
	}
};

export const NotificationItem: FC<NotificationItemProps> = ({ notification, markNotificationClicked }) => {
	const { userInfo } = useAuth();
	const navigate = useNavigate();
	const { getDepartmentDisplayName } = useHospitalData();
	const departmentColor = useDepartmentColor();
	const itemRef = useRef<HTMLDivElement>(null);
	const { t } = useTranslation();
	const parsedData = notification.parsed_data;
	const shiftType = parsedData?.shift_type || ShiftType.Day;
	const shiftDate = parsedData?.shift_date;

	useEffect(() => {
		const observer = new IntersectionObserver(
			(entries) => {
				entries.forEach((entry) => {
					if (entry.isIntersecting) {
						observer.disconnect();
					}
				});
			},
			{
				threshold: 0.5,
				rootMargin: "0px",
			},
		);

		if (itemRef.current) {
			observer.observe(itemRef.current);
		}

		return () => {
			observer.disconnect();
		};
	}, [notification.id, notification.notification_type, notification.isSeen, notification.isClicked, notification.created_at, shiftType, userInfo?.id]);

	// Tracking helper function
	const trackNotificationEvent = (eventName: string, additionalData: Record<string, any> = {}) => {
		if (!userInfo?.id) return;

		trackEvent(eventName, {
			user_id: userInfo.id,
			timestamp: new Date().toISOString(),
			session_id: sessionStorage.getItem("session_id") || "unknown",
			...additionalData,
		});
	};

	const handleClick = async () => {
		// Track initial interaction
		trackNotificationEvent("notification_item_interaction", {
			interaction_type: "click",
			notification_id: notification.id,
			notification_type: notification.notification_type,
			was_seen: notification.isSeen,
			was_clicked: notification.isClicked,
			shift_type: shiftType,
			shift_date: shiftDate ? shiftDate.toFormat("yyyy-MM-dd") : null,
			needed_nurses: parsedData?.needed_nurses,
		});

		try {
			// Track time of click relative to page load
			const pageEntryTime = sessionStorage.getItem("page_entry_time");
			const timeToClick = pageEntryTime ? Date.now() - Number.parseInt(pageEntryTime) : null;

			// Mark notification as clicked
			const success = await markNotificationClicked(notification.id);

			if (!success) {
				console.warn(`Failed to mark notification ${notification.id} as clicked`);
			}

			// Track the click event
			trackNotificationEvent("notification_clicked", {
				notification_id: notification.id,
				notification_type: notification.notification_type,
				click_successful: success,
				time_to_click_ms: timeToClick,
				notification_age: notification.created_at ? DateTime.now().diff(notification.created_at, "hour") : null,
				was_previously_seen: notification.isSeen,
			});

			if (notification.notification_type === NotificationType.DailyPickupSummary) {
				const requestsUrl = `/${notification.department_id}/requests`;
				trackNotificationEvent("navigation_from_notification", {
					destination: "requests",
					destination_url: requestsUrl,
					notification_type: notification.notification_type,
				});
				navigate(requestsUrl);
				return;
			}

			// Handle PerformanceReport notifications first (they don't have parsed_data)
			if (notification.notification_type === NotificationType.PerformanceReport) {
				const managementCenterUrl = `/${notification.department_id}/management-center`;
				trackNotificationEvent("navigation_from_notification", {
					destination: "management_center",
					destination_url: managementCenterUrl,
					notification_type: notification.notification_type,
				});
				navigate(managementCenterUrl);
				return;
			}

			// The Employee Status Review digest carries no shift_date, so it has to be routed before
			// the shift-date switch below: it deep-links to the Team page pre-filtered to the pending
			// decisions — the same view the toolbar's "View and Decide" button opens.
			if (notification.notification_type === NotificationType.EmployeeStatusDecision) {
				const teamDecisionsUrl = buildTeamNeedsDecisionUrl(notification.department_id);
				trackNotificationEvent("navigation_from_notification", {
					destination: "team_needs_decision",
					destination_url: teamDecisionsUrl,
					notification_type: notification.notification_type,
				});
				navigate(teamDecisionsUrl);
				return;
			}

			// Get parsed data from notification
			if (!parsedData) {
				// Fallback to open shifts page if no parsed data
				trackNotificationEvent("navigation_from_notification", {
					destination: "/open-shifts",
					notification_type: notification.notification_type,
					reason: "fallback_no_parsed_data",
					notification_id: notification.id,
				});
				navigate("/open-shifts");
				return;
			}

			// Navigate based on notification type
			switch (notification.notification_type) {
				case NotificationType.NurseTermination: {
					const newShiftUrl = `/nurses/${parsedData.nurse_id}`;
					trackNotificationEvent("navigation_from_notification", {
						destination: "nurses",
						destination_url: newShiftUrl,
						notification_type: notification.notification_type,
						highlight_date: parsedData.shift_date.toFormat("yyyy-MM-dd"),
						highlight_shift_type: parsedData.shift_type,
					});
					navigate(newShiftUrl);
					break;
				}

				case NotificationType.OpenShift: {
					const newShiftUrl = `/open-shifts?highlight=${parsedData.shift_date.toFormat("yyyy-MM-dd")}&shift_type=${parsedData.shift_type}`;
					trackNotificationEvent("navigation_from_notification", {
						destination: "open_shifts",
						destination_url: newShiftUrl,
						notification_type: notification.notification_type,
						highlight_date: parsedData.shift_date.toFormat("yyyy-MM-dd"),
						highlight_shift_type: parsedData.shift_type,
					});
					navigate(newShiftUrl);
					break;
				}

				case NotificationType.ApprovedReplace: {
					const approvedReplaceUrl = `/open-shifts?highlight=${parsedData.shift_date.toFormat("yyyy-MM-dd")}&shift_type=${parsedData.shift_type}`;
					trackNotificationEvent("navigation_from_notification", {
						destination: "open_shifts",
						destination_url: approvedReplaceUrl,
						notification_type: notification.notification_type,
						highlight_date: parsedData.shift_date.toFormat("yyyy-MM-dd"),
						highlight_shift_type: parsedData.shift_type,
					});
					navigate(approvedReplaceUrl);
					break;
				}

				case NotificationType.DeclinedReplace: {
					const declinedReplaceUrl = `/my-schedule?date=${parsedData.shift_date.toFormat("yyyy-MM-dd")}`;
					trackNotificationEvent("navigation_from_notification", {
						destination: "my_schedule",
						destination_url: declinedReplaceUrl,
						notification_type: notification.notification_type,
						date_param: parsedData.shift_date.toFormat("yyyy-MM-dd"),
					});
					navigate(declinedReplaceUrl);
					break;
				}

				case NotificationType.Declined: {
					const declinedUrl = `/my-schedule?date=${parsedData.shift_date.toFormat("yyyy-MM-dd")}`;
					trackNotificationEvent("navigation_from_notification", {
						destination: "my_schedule",
						destination_url: declinedUrl,
						notification_type: notification.notification_type,
						date_param: parsedData.shift_date.toFormat("yyyy-MM-dd"),
					});
					navigate(declinedUrl);
					break;
				}

				case NotificationType.Approved: {
					const approvedUrl = `/my-schedule?date=${parsedData.shift_date.toFormat("yyyy-MM-dd")}`;
					trackNotificationEvent("navigation_from_notification", {
						destination: "my_schedule",
						destination_url: approvedUrl,
						notification_type: notification.notification_type,
						date_param: parsedData.shift_date.toFormat("yyyy-MM-dd"),
					});
					navigate(approvedUrl);
					break;
				}

				case NotificationType.AddedToShift: {
					const addedUrl = `/my-schedule?date=${parsedData.shift_date.toFormat("yyyy-MM-dd")}`;
					trackNotificationEvent("navigation_from_notification", {
						destination: "my_schedule",
						destination_url: addedUrl,
						notification_type: notification.notification_type,
						date_param: parsedData.shift_date.toFormat("yyyy-MM-dd"),
					});
					navigate(addedUrl);
					break;
				}

				case NotificationType.RemovedFromShift: {
					const removedUrl = `/my-schedule?date=${parsedData.shift_date.toFormat("yyyy-MM-dd")}`;
					trackNotificationEvent("navigation_from_notification", {
						destination: "my_schedule",
						destination_url: removedUrl,
						notification_type: notification.notification_type,
						date_param: parsedData.shift_date.toFormat("yyyy-MM-dd"),
					});
					navigate(removedUrl);
					break;
				}

				case NotificationType.SchedulePublished:
				case NotificationType.CallOff: {
					const callOffUrl = `/${notification.department_id}/staffing/${parsedData.shift_date.toFormat("yyyy-MM-dd")}`;
					trackNotificationEvent("navigation_from_notification", {
						destination: "staffing",
						destination_url: callOffUrl,
						notification_type: notification.notification_type,
						date_param: parsedData.shift_date.toFormat("yyyy-MM-dd"),
					});
					navigate(callOffUrl);
					break;
				}
				case NotificationType.DailyPickupSummary: {
					const requestsUrl = `/${notification.department_id}/requests`;
					trackNotificationEvent("navigation_from_notification", {
						destination: "requests",
						destination_url: requestsUrl,
						notification_type: notification.notification_type,
					});
					navigate(requestsUrl);
					break;
				}

				default: {
					const defaultUrl = `/my-schedule?date=${parsedData.shift_date.toFormat("yyyy-MM-dd")}`;
					trackNotificationEvent("navigation_from_notification", {
						destination: "my_schedule",
						destination_url: defaultUrl,
						notification_type: notification.notification_type,
						date_param: parsedData.shift_date.toFormat("yyyy-MM-dd"),
					});
					navigate(defaultUrl);
					break;
				}
			}
		} catch (error) {
			if (userInfo?.id) {
				const errorData = {
					notification_id: notification.id,
					error_details: (error as Error).message,
				};

				trackErrorEvent("notification_click_failed", error, {
					user_id: userInfo.id,
					...errorData,
				});

				trackNotificationEvent("navigation_from_notification", {
					destination: "open_shifts",
					reason: "error_fallback",
					notification_type: notification.notification_type,
					error: true,
				});
			}

			navigate("/open-shifts");
		}
	};

	return (
		<Paper
			ref={itemRef}
			elevation={0}
			onClick={handleClick}
			onMouseEnter={() => {
				trackEvent("notification_item_interaction", {
					interaction_type: "hover",
					notification_id: notification.id,
					notification_type: notification.notification_type,
					was_seen: notification.isSeen,
					was_clicked: notification.isClicked,
					timestamp: new Date().toISOString(),
					user_id: userInfo?.id,
					session_id: sessionStorage.getItem("session_id") || "unknown",
				});
			}}
			sx={{
				width: "100%",
				p: 2,
				cursor: "pointer",
				borderRadius: 1,
				bgcolor: notification.isClicked ? "#FFFFFF" : notification.isSeen ? "rgba(211, 202, 217, 0.3)" : "rgba(201, 49, 206, 0.05)",
				borderLeft: notification.isSeen ? "none" : "4px solid #3182CE",
				transition: "background-color 0.2s ease",
				"&:hover": {
					bgcolor: "rgba(0, 0, 0, 0.02)",
				},
			}}
		>
			<Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
				<Box sx={{ flex: 1 }}>
					{/* Title and time ago */}
					<Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
						<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
							<Box
								sx={{
									display: "flex",
									alignItems: "center",
									justifyContent: "center",
									width: 28,
									height: 28,
									flexShrink: 0,
								}}
							>
								{getNotificationIcon(notification.notification_type, shiftType, notification.title)}
							</Box>

							<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
								<Typography
									variant="h6"
									sx={{
										fontWeight: 700,
										fontSize: "1rem",
										color: notification.isClicked ? "#64748B" : "#1E293B",
										whiteSpace: "nowrap",
										overflow: "hidden",
										textOverflow: "ellipsis",
									}}
								>
									{notification.title === "Replace Shift" ? t("open.shift") : notification.title}
								</Typography>
								{parsedData?.shift_date && notification.notification_type !== NotificationType.PerformanceReport && (
									<Box
										sx={{
											display: "flex",
											alignItems: "center",
											gap: 0.5,
											flexShrink: 0,
										}}
									>
										<Typography
											variant="h6"
											sx={{
												fontWeight: 500,
												fontSize: "1rem",
												color: "#64748B",
												whiteSpace: "nowrap",
											}}
										>
											{parsedData.shift_date.toFormat("ccc, MMM d")}
										</Typography>
										{parsedData?.shift_type === ShiftType.Day ? (
											<WbSunny sx={{ fontSize: "1.25rem", color: SHIFT_DAY }} />
										) : (
											<DarkMode sx={{ fontSize: "1.25rem", color: SHIFT_NIGHT_FALLBACK }} />
										)}
										{/* Bonus */}
										{!!notification.notification_data.bonus && <img src="/bonus.svg" alt="Bonus" style={{ marginTop: 4 }} />}
									</Box>
								)}
							</Box>
						</Box>
						<Tooltip title={notification.created_at.toFormat("MMM D, yyyy h:mm A")}>
							<Typography variant="caption" color="textSecondary" sx={{ whiteSpace: "nowrap", ml: 1 }}>
								{notification.created_at.toRelative()}{" "}
							</Typography>
						</Tooltip>
					</Box>
					<Box sx={{ display: "flex", alignItems: "center", mb: 0.5 }}>
						<Box
							sx={{
								bgcolor: departmentColor(notification.department_id).dot,
								width: "4px",
								height: "40px",
								borderRadius: "2px",
								mr: 1,
							}}
						/>
						<Box>
							<Typography
								variant="body2"
								sx={{
									color: (theme) => alpha(theme.palette.primary.dark, 0.5),
									fontSize: "0.875rem",
									fontWeight: 600,
								}}
							>
								{getDepartmentDisplayName(notification.department_id)}
							</Typography>
						</Box>
					</Box>
					{/* Message */}
					<Typography variant="body2" color="textSecondary" sx={{ mb: 1, ml: 4, fontSize: "0.875rem" }}>
						{notification.message}
					</Typography>

					{/* Additional information */}
					<Box sx={{ ml: 4 }}>
						{/* Location if present */}
						{parsedData?.location && (
							<Box sx={{ mb: 1 }}>
								<Typography variant="caption" color="textSecondary">
									{t("location")}: {parsedData.location}
								</Typography>
							</Box>
						)}
					</Box>
				</Box>
			</Box>
		</Paper>
	);
};
